"use client";

import * as React from "react";
import { useState } from "react";

import { Check, Copy } from "lucide-react";

import { cn } from "@repo/ui/lib/utils";

export interface CodeBlockProps {
  code?: string;
  children?: React.ReactElement;
  language?: string;
  showCopy?: boolean;
  className?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  children,
  language = "tsx",
  showCopy = true,
  className,
}) => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Extract code from React element if children provided
  const getCodeFromElement = (element: React.ReactElement): string => {
    const extractProps = (props: Record<string, unknown>): string => {
      const propsArray: string[] = [];

      Object.entries(props).forEach(([key, value]) => {
        if (key === "children") {
          return; // Handle children separately
        }

        if (typeof value === "string") {
          propsArray.push(`  ${key}="${value}"`);
        } else if (React.isValidElement(value)) {
          // Handle React elements like icons
          const elementName = (value.type as { name?: string })?.name || "Icon";
          propsArray.push(`  ${key}={<${elementName} className="w-4 h-4" />}`);
        } else if (typeof value === "boolean") {
          propsArray.push(`  ${key}={${value}}`);
        } else if (typeof value === "number") {
          propsArray.push(`  ${key}={${value}}`);
        }
      });

      return propsArray.join("\n");
    };

    const componentName =
      (element.type as { displayName?: string; name?: string })?.displayName ||
      (element.type as { displayName?: string; name?: string })?.name ||
      "Component";
    const elementProps = element.props as Record<string, unknown>;
    const props = extractProps(elementProps);
    const hasChildren = elementProps.children;

    if (hasChildren && props) {
      return `<${componentName} \n${props}\n>\n  ${elementProps.children}\n</${componentName}>`;
    } else if (props) {
      return `<${componentName} \n${props}\n />`;
    } else if (hasChildren) {
      return `<${componentName}>\n  ${elementProps.children}\n</${componentName}>`;
    } else {
      return `<${componentName} />`;
    }
  };

  const codeToDisplay = code || (children ? getCodeFromElement(children) : "");

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(codeToDisplay);
      setCopiedCode(codeToDisplay);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  return (
    <div className={cn("relative rounded-lg bg-gray-900 p-4", className)}>
      <pre className="overflow-x-auto text-sm text-gray-100">
        <code className={`language-${language}`}>{codeToDisplay}</code>
      </pre>
      {showCopy && (
        <button
          onClick={copyToClipboard}
          className="absolute top-2 right-2 rounded bg-gray-700 p-2 transition-colors hover:bg-gray-600"
          title="Copy code"
        >
          {copiedCode === codeToDisplay ? (
            <Check className="h-4 w-4 text-green-400" />
          ) : (
            <Copy className="h-4 w-4 text-gray-300" />
          )}
        </button>
      )}
    </div>
  );
};
